<template>
  <ViewContainer :title="$t('menu.site_settings')" :breadcrumb="breadcrumbs">
    <v-row class="mt-n4">
      <v-col cols="12" sm="12" md="4" lg="3">
        <v-sheet
          class="rounded pa-2"
          data-testid="siteSettings-sheet-menuPanel"
          elevation="2"
        >
          <template v-if="hasMultiSiteConfigured">
            <div
              class="pa-2"
              data-testid="siteSettings-div-multiSiteSelector"
            >
              <p
                data-testid="siteSettings-text-multiSiteSupport"
                class="subtitle-2 font-weight-600 pa-0 mb-1 blue-grey--text text--darken-3"
              >
                {{ $t('site_settings.multi_site_support') }}
              </p>

              <SitesSearchPopover
                class="cursor-pointer"
                data-testid="siteSettings-select-site"
                localSearch
                :allowEmptySelection="false"
                :availableLocalSites="selectableSites"
                :disabled="isLoading"
                :multiple="false"
                :selectedSiteIds="selectedSiteIds"
                :showAdvancedOption="false"
                @change="handleSiteChange"
              >
                <v-text-field
                  append-icon="mdi-chevron-down"
                  block
                  class="cursor-pointer"
                  data-testid="siteSettings-siteSelector-select"
                  dense
                  hide-details
                  outlined
                  readonly
                  :disabled="isLoading"
                  :loading="isLoading"
                  :value="selectedSiteName"
                />
              </SitesSearchPopover>
            </div>

            <v-divider
              v-if="hasMultiSiteConfigured"
              class="my-2"
              data-testid="siteSettings-divider-siteSelector"
            />
          </template>

          <v-list nav>
            <v-list-item-group
              mandatory
              color="primary"
              data-testid="siteSettings-list-menu"
              v-model="option"
            >
              <v-list-item
                v-for="option in options"
                :key="option.id"
                :value="option.key"
              >
                <v-list-item-content :data-tourid="option.tourid">
                  <v-list-item-title>
                    {{ option.text }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-sheet>
      </v-col>

      <v-col v-if="site" cols="12" sm="12" md="8" lg="7" class="mb-8">
        <General
          data-testid="siteSettings-general"
          v-show="option === OPTIONS.GENERAL"
          :site="site"
          :siteHasChanged="siteHasChanged"
          :isLoading="isLoading"
          :organizationId="organizationId"
          @update="updateSite"
          @reset="resetChanges"
        />
        <Categories
          data-testid="siteSettings-component-categories"
          v-show="option === OPTIONS.CATEGORIES"
          :key="siteId"
          :siteId="siteId"
          :organizationId="organizationId"
        />
        <SituationTypes
          v-show="option === OPTIONS.SITUATION_TYPES"
          :key="`situation-${siteId}`"
          :siteId="siteId"
          :organizationId="organizationId"
        />
      </v-col>
    </v-row>

    <v-dialog
      v-model="showDialog"
      data-testid="siteSettings-dialog-confirmLeave"
      max-width="315"
    >
      <v-card>
        <v-card-title class="pb-0">
          <span class="h6 grey--text text--darken-4">{{
            $t('before_leave_dialog.dialog_header')
          }}</span></v-card-title
        >
        <v-card-text class="pb-3">
          <span class="grey--text text--darken-4">
            {{ $t('before_leave_dialog.dialog_subtitle') }}</span
          >
        </v-card-text>
        <v-card-actions class="pb-3 justify-end">
          <Button
            plain
            data-testid="siteSettingsDialog-button-cancel"
            class="me-6"
            color="grey"
            :text="$t('common.button.cancel')"
            @click="cancelLeaveRoute"
          />
          <Button
            plain
            class="me-5"
            data-testid="siteSettingsDialog-button-save"
            color="primary"
            :text="$t('common.button.save')"
            @click="updateSite()"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </ViewContainer>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'

import { utils } from 'frontend-js-shared'

import Button from '@/components/UI/Button'
import SitesSearchPopover from '@/components/SitesSearchPopover/SitesSearchPopover.vue'
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'

import mixin from '@/mixins/mixin.vue'

import Categories from './Categories.vue'
import General from './General.vue'
import SituationTypes from './SituationTypes.vue'

const OPTIONS = {
  GENERAL: 'general',
  CATEGORIES: 'categories',
  SITUATION_TYPES: 'situations',
}

export default {
  name: 'SiteSettingsView',
  mixins: [mixin],
  components: {
    Button,
    ViewContainer,
    General,
    Categories,
    SituationTypes,
    SitesSearchPopover,
  },

  props: {
    breadcrumbs: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  data() {
    return {
      showDialog: false,
      option: null,
      isLoading: true,
      options: [
        {
          key: OPTIONS.GENERAL,
          text: this.$t('settings.general_settings'),
          tourid: 'siteSettings-option-settings',
        },
        {
          key: OPTIONS.CATEGORIES,
          text: this.$t('common.category_other'),
          tourid: 'siteSettings-option-categories',
        },
        {
          key: OPTIONS.SITUATION_TYPES,
          text: this.$t('settings.situation_types'),
          tourid: 'siteSettings-option-situationTypes',
        },
      ],
      site: {},
      OPTIONS,
      siteId: this.getRouteParams().siteId,
      organizationId: this.getRouteParams().organizationId,
      siteHasChanged: false,
      router: null,
      originalSiteData: {},
      selectedSite: null,
      pendingSiteId: null,
    }
  },

  computed: {
    ...mapGetters(['account', 'authorizedSiteDetails']),

    selectableSites() {
      return this.authorizedSiteDetails.map(site => ({
        ...site,
        id: utils.getEntityId(site),
      }))
    },

    hasMultiSiteConfigured() {
      if (
        !utils.isSiteAdmin(this.account) ||
        this.account?.isOrganizationAdmin ||
        this.account?.isAdmin
      )
        return false
      return this.selectableSites.length > 1
    },

    selectedSiteIds() {
      return this.siteId ? [this.siteId] : []
    },

    selectedSiteName() {
      return this.selectedSite?.name || this.site?.name || ''
    },
  },

  async mounted() {
    await this.getSite()
    this.option = Object.values(this.OPTIONS)[0]
  },

  watch: {
    '$route.params.siteId'(newSiteId) {
      if (!newSiteId || utils.isSameId(newSiteId, this.siteId)) return

      this.siteId = newSiteId
      this.siteHasChanged = false
      this.getSite()
    },
    site: {
      deep: true,
      handler(newValue, oldValue) {
        if (oldValue._id && !_.isEqual(newValue, this.originalSiteData)) {
          this.siteHasChanged = true
        }
      },
    },
  },

  methods: {
    ...mapActions({
      get: 'siteSettings/getSite',
      update: 'siteSettings/updateSite',
    }),

    handleSiteChange({ ids, items }) {
      if (_.isEmpty(ids)) return

      const newSiteId = ids[0]
      if (utils.isSameId(newSiteId, this.siteId)) return

      if (this.siteHasChanged) {
        this.selectedSite = items[0]
        this.pendingSiteId = newSiteId
        this.showDialog = true
        return
      }

      this.navigateToSite(newSiteId)
    },

    navigateToSite(siteId) {
      this.$router.replace({
        name: 'site-settings',
        params: {
          ...this.$route.params,
          siteId,
        },
      })
    },

    async getSite() {
      this.isLoading = true
      try {
        const response = await this.get({
          siteId: this.siteId,
          organizationId: this.organizationId,
        })

        this.originalSiteData = Object.freeze({ ...response.data })

        this.selectedSite = response.data
        this.site = response.data
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async updateSite() {
      try {
        this.isLoading = true
        const data = { ...this.site }

        await this.update({
          organizationId: this.organizationId,
          siteId: this.siteId,
          site: data,
        })
        await this.getSite()
        this.showSuccess(this.$t('site_settings.site_saved_successfully'))
        this.siteHasChanged = false
        this.showDialog = false
        if (this.router?.next) return this.router.next()

        const { pendingSiteId } = this
        this.pendingSiteId = null
        if (pendingSiteId) this.navigateToSite(pendingSiteId)
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },
    resetChanges() {
      this.site = {
        ...this.originalSiteData,
      }
      this.siteHasChanged = false
    },
    cancelLeaveRoute() {
      this.selectedSite = this.originalSiteData
      this.showDialog = false
      this.router = null
      this.pendingSiteId = null
    },
  },

  beforeDestroy() {
    this.site = {}
  },

  beforeRouteLeave(to, from, next) {
    if (this.siteHasChanged) {
      this.showDialog = true
      this.router = { to, from, next }
      return next(false)
    } else {
      return next()
    }
  },
}
</script>
